import { IconAlertTriangle, IconArrowLeft, IconExternalLink, IconFileText, IconInfoCircle, IconMapPin, IconPhone, IconSparkles, IconStar, IconSteeringWheel } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { api, ApiError, type Driver, type ScheduleChange, type TimelineItem, type TripDetail } from "../api";
import { CompanionsCard } from "../components/CompanionsCard";
import { HelpBar, HelpNow, helpAvailable } from "../components/HelpNow";
import { BrandChannels } from "../components/Footer";
import { kindIcon } from "../components/kindIcon";
import { NoticesBanner } from "../components/NoticesBanner";
import { PaymentsCard } from "../components/PaymentsCard";
import { RequestsCard } from "../components/RequestsCard";
import { TravellersCard } from "../components/TravellersCard";
import { Card, Empty, ErrorNote, SectionTitle, Spinner, StatusChip } from "../components/ui";
import { dayKey, formatDate, formatDateRange, formatDateTime, formatDayHeading, formatMoney, formatTime } from "../format";
import type { Key } from "../i18n";
import { useSession } from "../session";
import { WELCOME_STORAGE } from "./Proposal";

export function TripPage({ id }: { id: string }) {
  const { brand, lang, t } = useSession();
  const [detail, setDetail] = useState<TripDetail | undefined>(undefined);
  const [error, setError] = useState<{ message: string; signIn?: boolean } | null>(null);
  const [tick, setTick] = useState(0);
  const [welcome, setWelcome] = useState(false);
  const [openPay] = useState(() => window.location.hash === "#pay");
  const [helpOpen, setHelpOpen] = useState(() => window.location.hash === "#help");

  // The one-time welcome after accepting a proposal: set by the proposal page, consumed here.
  useEffect(() => {
    try {
      if (sessionStorage.getItem(WELCOME_STORAGE) === id) {
        sessionStorage.removeItem(WELCOME_STORAGE);
        setWelcome(true);
      }
    } catch {
      // Storage blocked: no banner.
    }
  }, [id]);

  useEffect(() => {
    setError(null);
    api
      .trip(id)
      .then(setDetail)
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.code === "sign_in_required") setError({ message: t("linkSessionNote"), signIn: true });
        else if (err instanceof ApiError && err.status === 404) setError({ message: t("notFoundTitle") });
        else setError({ message: t("somethingWrong") });
      });
  }, [id, tick, t]);

  if (error) {
    return (
      <main className="page">
        <a href="/" className="link-button back-link">
          <IconArrowLeft size={16} /> {t("back")}
        </a>
        <ErrorNote onRetry={error.signIn ? undefined : () => setTick((n) => n + 1)}>{error.message}</ErrorNote>
        {error.signIn && (
          <a href="/signin" className="button button-primary">
            {t("signIn")}
          </a>
        )}
      </main>
    );
  }
  if (!detail) {
    return (
      <main className="page">
        <Spinner />
      </main>
    );
  }

  const { trip, timeline, members, role } = detail;
  const services = trip.data.services ?? [];
  const managed = trip.managed ?? trip.source !== "legacy";
  const holder = role === "holder";
  const live = trip.status === "confirmed" || trip.status === "in_progress";
  const helpLive = helpAvailable(trip.status, trip.endsOn);
  const inProgress = trip.status === "in_progress";
  const travellersLabel = trip.travellers === 1 ? t("traveller") : t("travellers", { n: trip.travellers });

  // Grouped by local day; "today" in the trip's zone and the next item still ahead are marked.
  const now = new Date();
  const todayKey = dayKey(now.toISOString(), trip.timezone);
  const startOf = (item: TimelineItem) => (item.startsAt ? new Date(item.startsAt).getTime() : Number.POSITIVE_INFINITY);
  const sorted = [...timeline].sort((a, b) => startOf(a) - startOf(b));
  const nextId = sorted.find((i) => i.status !== "cancelled" && i.startsAt && new Date(i.startsAt) > now)?.id ?? null;

  const days = new Map<string, TimelineItem[]>();
  for (const item of sorted) {
    const key = dayKey(item.startsAt, item.tz || trip.timezone);
    const list = days.get(key) ?? [];
    list.push(item);
    days.set(key, list);
  }
  const dayKeys = [...days.keys()].sort((a, b) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)));

  return (
    <main className={`page ${inProgress ? "page-with-bar" : ""}`}>
      <a href="/" className="link-button back-link">
        <IconArrowLeft size={16} /> {t("back")}
      </a>
      <header className="trip-header">
        <StatusChip status={trip.status} lang={lang} />
        <h1>{trip.title}</h1>
        <p className="muted">
          {trip.startsOn ? formatDateRange(trip.startsOn, trip.endsOn, lang) : t("noDate")}
          {trip.destination ? ` · ${trip.destination}` : ""}
        </p>
        <p className="muted small">
          {t("reference")} {trip.reference} · {travellersLabel}
          {trip.totalMinor > 0 ? ` · ${t("total")} ${formatMoney(trip.totalMinor, trip.currency, lang)}` : ""}
        </p>
        {!managed && (
          <p className="notice small">
            <IconInfoCircle size={16} /> {t("legacyNote")}
          </p>
        )}
        {managed && trip.source === "legacy" && (
          <p className="notice small">
            <IconInfoCircle size={16} /> {t("legacyImported", { ref: trip.data.legacy?.bookingRef ?? trip.reference })}
          </p>
        )}
      </header>

      {welcome && (
        <div className="notice welcome">
          <IconSparkles size={20} />
          <div className="notice-text">
            <strong>{t("welcomeTrip")}</strong>
            <span className="small">{t("welcomeTripBody")}</span>
          </div>
        </div>
      )}

      {holder && managed && live && <NoticesBanner />}

      {managed && <PaymentsCard tripId={trip.id} holder={holder} openPay={openPay} />}
      {managed && <TravellersCard tripId={trip.id} />}
      {(detail.reviews?.length ?? 0) > 0 && (
        <>
          <SectionTitle>{t("reviewTitle")}</SectionTitle>
          <Card>
            <ul className="list">
              {detail.reviews!.map((r) => (
                <li key={r.propertyId} className="list-row">
                  <span className="list-text">
                    <strong>{r.propertyName}</strong>
                    <span className="muted small">{t("reviewInvite", { hotel: r.propertyName, dates: `${formatDate(r.checkIn, lang)} – ${formatDate(r.checkOut, lang)}` })}</span>
                    <span className="muted small">{t("reviewOpenUntil", { date: formatDate(r.expiresAt.slice(0, 10), lang) })}</span>
                  </span>
                  <a href={r.url} className="button button-small doc-button" target="_blank" rel="noreferrer">
                    <IconStar size={16} /> {t("reviewWrite")} <IconExternalLink size={14} />
                  </a>
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}
      {(detail.documents?.length ?? 0) > 0 && (
        <>
          <SectionTitle>{t("tripDocuments")}</SectionTitle>
          <Card>
            <ul className="list">
              {detail.documents!.map((d) => (
                <li key={d.id ?? d.url} className="list-row">
                  <span className="list-text">
                    <strong>{d.name}</strong>
                    {d.kind === "pre_contract" && d.acceptedAt && <span className="muted small">{t("preContractAccepted", { version: d.version ?? 1, date: formatDate(d.acceptedAt.slice(0, 10), lang) })}</span>}
                  </span>
                  {d.url && (
                    <a href={d.url} className="button button-small doc-button" target="_blank" rel="noreferrer">
                      <IconFileText size={16} /> {t("document")} <IconExternalLink size={14} />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}

      <SectionTitle>{t("timeline")}</SectionTitle>
      {timeline.length === 0 ? (
        <Empty title={t("timelineEmpty")} />
      ) : (
        dayKeys.map((key) => {
          const today = key !== "" && key === todayKey;
          return (
            <section key={key || "undated"} className={`day ${today ? "day-today" : ""}`}>
              <h3 className="day-heading">
                {key ? formatDayHeading(key, lang) : t("noDate")}
                {today && <span className="chip chip-today">{t("today")}</span>}
              </h3>
              <div className="stack">
                {(days.get(key) ?? []).map((item) => (
                  <TimelineCard key={item.id} item={item} tz={item.tz || trip.timezone} next={item.id === nextId} />
                ))}
              </div>
            </section>
          );
        })
      )}

      <SectionTitle>{t("services")}</SectionTitle>
      {services.length === 0 ? (
        <Empty title={t("servicesEmpty")} />
      ) : (
        <Card>
          <ul className="list">
            {services.map((s) => (
              <li key={s.id} className="list-row">
                <span className="list-icon">{kindIcon(s.kind)}</span>
                <span className="list-text">
                  <strong>{s.title}</strong>
                  <span className="muted small">{s.status}</span>
                </span>
                {s.sell && <span className="list-aside">{formatMoney(s.sell.amountMinor, s.sell.currency, lang)}</span>}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <SectionTitle>{t("companions")}</SectionTitle>
      <CompanionsCard key={`${trip.id}:${members.length}`} tripId={trip.id} members={members} holder={holder && managed} holderName={trip.holderName} />

      {managed && (
        <>
          <SectionTitle>{t("requests")}</SectionTitle>
          <RequestsCard tripId={trip.id} holder={holder} />
        </>
      )}

      <SectionTitle>{t("help")}</SectionTitle>
      <Card className="help-card">
        <p className="small">{t("helpBody")}</p>
        {brand && <BrandChannels brand={brand} compact />}
        <HelpNow tripId={trip.id} available={helpLive} open={helpOpen && helpLive} onOpen={() => setHelpOpen(true)} onClose={() => setHelpOpen(false)} />
      </Card>
      {inProgress && <HelpBar onOpen={() => setHelpOpen(true)} />}
    </main>
  );
}

const str = (v: unknown): string | null => (typeof v === "string" && v ? v : typeof v === "number" ? String(v) : null);
const obj = (v: unknown): Record<string, unknown> | null => (v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, unknown>) : null);

/** `payload.scheduleChange` as the Flights app leaves it (services/live.ts), or null when absent or malformed. */
function scheduleChangeOf(p: Record<string, unknown>): ScheduleChange | null {
  const raw = obj(p.scheduleChange);
  if (!raw) return null;
  const from = obj(raw.from) ?? {};
  return { from: { startsAt: str(from.startsAt), endsAt: str(from.endsAt) }, at: str(raw.at) ?? "", note: str(raw.note) };
}

/** `payload.driver` as the Transfers app leaves it. */
function driverOf(p: Record<string, unknown>): Driver | null {
  const raw = obj(p.driver);
  const name = raw ? str(raw.name) : null;
  if (!raw || !name) return null;
  return { name, vehicle: str(raw.vehicle), plate: str(raw.plate), phone: str(raw.phone), liveUrl: str(raw.liveUrl), status: str(raw.status) ?? "assigned", at: str(raw.at) ?? "" };
}

interface Segment { flight?: string; from?: string; to?: string; departure?: string; arrival?: string; terminal?: string }

function TimelineCard({ item, tz, next }: { item: TimelineItem; tz: string; next: boolean }) {
  const { lang, t } = useSession();
  const p = item.payload;
  const rows: { key: Key; value: string }[] = [];
  const push = (key: Key, value: unknown) => {
    const s = str(value);
    if (s) rows.push({ key, value: s });
  };

  let segments: Segment[] = [];
  let change: ScheduleChange | null = null;
  let driver: Driver | null = null;
  if (item.kind === "flight") {
    segments = Array.isArray(p.segments) ? (p.segments as Segment[]) : [];
    push("pnr", p.pnr);
    change = scheduleChangeOf(p);
  } else if (item.kind === "accommodation") {
    push("room", p.room);
    push("board", p.board);
    push("checkIn", p.checkIn);
    push("checkOut", p.checkOut);
    if (typeof p.rooms === "number" && p.rooms > 1) rows.push({ key: "rooms", value: t("rooms", { n: p.rooms }) });
    push("reference", p.reference);
  } else if (item.kind === "transfer" || item.kind === "car") {
    push("vehicle", p.vehicle ?? p.type);
    push("flightNumber", p.flightNumber);
    push("pickup", p.pickup ?? p.from);
    push("dropoff", p.dropoff ?? p.to);
    push("supplier", p.supplier);
    driver = driverOf(p);
  } else {
    push("meetingPoint", p.meetingPoint);
    push("duration", p.duration);
    push("supplier", p.supplier);
    push("reference", p.reference);
  }
  const note = str(p.note) ?? str(p.description);

  const time = item.startsAt ? formatTime(item.startsAt, lang, tz) : "";
  const end = item.endsAt && item.kind !== "accommodation" ? formatTime(item.endsAt, lang, tz) : "";
  const changed = item.status === "changed" && change !== null;
  const enRoute = item.status === "en_route" || driver?.status === "en_route";

  // The old times: hours only when still the same day, else with the date.
  const oldTimes = (() => {
    if (!change) return "";
    const sameDay = !change.from.startsAt || !item.startsAt || dayKey(change.from.startsAt, tz) === dayKey(item.startsAt, tz);
    const fmt = (iso: string | null) => (iso ? (sameDay ? formatTime(iso, lang, tz) : formatDateTime(iso, lang, tz)) : "");
    const a = fmt(change.from.startsAt);
    const b = fmt(change.from.endsAt);
    return a && b ? `${a} – ${b}` : a || b;
  })();

  return (
    <Card className={`timeline-card timeline-${item.kind} ${next ? "timeline-next" : ""} ${enRoute ? "timeline-live" : ""}`}>
      <div className="timeline-head">
        <span className="list-icon">{kindIcon(item.kind)}</span>
        <div className="list-text">
          <strong>{item.title}</strong>
          {(time || end) && (
            <span className={`small ${changed ? "times-new" : "muted"}`}>
              {time}
              {end ? ` – ${end}` : ""}
            </span>
          )}
          {changed && oldTimes && (
            <span className="muted small times-old">
              {t("wasTimes")} <s>{oldTimes}</s>
            </span>
          )}
        </div>
        <span className="timeline-chips">
          {next && <span className="chip chip-next">{t("nextUp")}</span>}
          {item.status && item.status !== "confirmed" && <StatusChip status={item.status} lang={lang} />}
        </span>
      </div>
      {changed && change && (
        <div className="live-note live-changed">
          <IconAlertTriangle size={18} />
          <div className="notice-text small">
            <strong>{t("scheduleChanged")}</strong>
            {change.note && <span>{change.note}</span>}
          </div>
        </div>
      )}
      {segments.length > 0 && (
        <ul className="segments">
          {segments.map((s, i) => (
            <li key={i} className="segment">
              <span className="segment-route">
                <strong>{s.from ?? "?"}</strong> → <strong>{s.to ?? "?"}</strong>
              </span>
              <span className="muted small">
                {s.flight ? `${t("flight")} ${s.flight} · ` : ""}
                {s.departure ? formatTime(s.departure, lang, tz) : ""}
                {s.arrival ? ` – ${formatTime(s.arrival, lang, tz)}` : ""}
                {s.terminal ? ` · ${t("terminal")} ${s.terminal}` : ""}
              </span>
            </li>
          ))}
        </ul>
      )}
      {rows.length > 0 && (
        <dl className="details">
          {rows.map((r) => (
            <div key={r.key} className="detail">
              <dt>{t(r.key)}</dt>
              <dd>{r.key === "meetingPoint" ? <span className="inline-icon"><IconMapPin size={14} /> {r.value}</span> : r.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {driver && (
        <div className={`driver-card ${enRoute ? "driver-en-route" : ""}`}>
          <div className="driver-head">
            <span className="list-icon">
              <IconSteeringWheel size={20} stroke={1.5} />
            </span>
            <div className="list-text">
              <strong>{driver.name}</strong>
              <span className="muted small">
                {t("driver")}
                {driver.vehicle ? ` · ${driver.vehicle}` : ""}
                {driver.plate ? ` · ${t("plate")} ${driver.plate}` : ""}
              </span>
              {enRoute && (
                <span className="small on-the-way">
                  <span className="pulse" aria-hidden="true" /> {t("driverOnTheWay")}
                </span>
              )}
            </div>
          </div>
          <div className="row-buttons">
            {enRoute && driver.liveUrl && (
              <a href={driver.liveUrl} className="button button-primary button-small" target="_blank" rel="external noreferrer">
                <IconMapPin size={16} /> {t("followLive")} <IconExternalLink size={14} />
              </a>
            )}
            {driver.phone && (
              <a href={`tel:${driver.phone.replace(/\s+/g, "")}`} className="button button-small" rel="external">
                <IconPhone size={16} /> {t("callDriver")}
              </a>
            )}
          </div>
        </div>
      )}
      {note && <p className="small">{note}</p>}
      {item.documentUrl && (
        <div>
          <a href={item.documentUrl} className="button button-small doc-button" target="_blank" rel="external noreferrer">
            <IconFileText size={16} /> {t("document")} <IconExternalLink size={14} />
          </a>
        </div>
      )}
    </Card>
  );
}
